export const PIERCING_TYPES = [
  { id: "earlobe", displayName: "Earlobe", priceMin: 30, priceMax: 50, jewelryUpcharge: 20, sortOrder: 1 },
  { id: "helix", displayName: "Helix (Cartilage)", priceMin: 40, priceMax: 60, jewelryUpcharge: 25, sortOrder: 2 },
  { id: "tragus", displayName: "Tragus", priceMin: 45, priceMax: 70, jewelryUpcharge: 25, sortOrder: 3 },
  { id: "daith", displayName: "Daith", priceMin: 50, priceMax: 75, jewelryUpcharge: 30, sortOrder: 4 },
  { id: "conch", displayName: "Conch", priceMin: 50, priceMax: 75, jewelryUpcharge: 30, sortOrder: 5 },
  { id: "industrial", displayName: "Industrial", priceMin: 60, priceMax: 90, jewelryUpcharge: 35, sortOrder: 6 },
  { id: "rook", displayName: "Rook", priceMin: 50, priceMax: 75, jewelryUpcharge: 30, sortOrder: 7 },
  { id: "forward_helix", displayName: "Forward Helix", priceMin: 45, priceMax: 70, jewelryUpcharge: 25, sortOrder: 8 },
  { id: "nostril", displayName: "Nostril", priceMin: 40, priceMax: 65, jewelryUpcharge: 20, sortOrder: 9 },
  { id: "septum", displayName: "Septum", priceMin: 50, priceMax: 75, jewelryUpcharge: 25, sortOrder: 10 },
  { id: "bridge", displayName: "Bridge", priceMin: 55, priceMax: 80, jewelryUpcharge: 30, sortOrder: 11 },
  { id: "navel", displayName: "Navel (Belly Button)", priceMin: 50, priceMax: 75, jewelryUpcharge: 30, sortOrder: 12 },
  { id: "nipple", displayName: "Nipple", priceMin: 60, priceMax: 90, jewelryUpcharge: 35, sortOrder: 13 },
  { id: "eyebrow", displayName: "Eyebrow", priceMin: 45, priceMax: 70, jewelryUpcharge: 25, sortOrder: 14 },
  { id: "lip", displayName: "Lip / Labret", priceMin: 45, priceMax: 70, jewelryUpcharge: 25, sortOrder: 15 },
  { id: "tongue", displayName: "Tongue", priceMin: 50, priceMax: 75, jewelryUpcharge: 30, sortOrder: 16 },
  { id: "dermal", displayName: "Dermal / Surface", priceMin: 70, priceMax: 100, jewelryUpcharge: 40, sortOrder: 17 },
  { id: "other", displayName: "Other / Ask Us", priceMin: 40, priceMax: 100, jewelryUpcharge: 25, sortOrder: 18 },
] as const;

export type PiercingTypeId = typeof PIERCING_TYPES[number]["id"];

export const JEWELRY_MATERIALS = [
  { id: "implant_titanium", label: "Implant-Grade Titanium", description: "Hypoallergenic · Best for new piercings", multiplier: 1.0 },
  { id: "implant_steel", label: "Implant-Grade Steel", description: "Durable · Great for healed piercings", multiplier: 0.9 },
  { id: "solid_gold_14k", label: "14k Solid Gold", description: "Premium · Yellow, white, or rose gold", multiplier: 1.6 },
  { id: "solid_gold_18k", label: "18k Solid Gold", description: "Luxury · Highest quality gold option", multiplier: 2.0 },
  { id: "niobium", label: "Niobium", description: "Hypoallergenic alternative to titanium", multiplier: 1.05 },
  { id: "glass", label: "Glass / Pyrex", description: "Body-safe · Unique styles", multiplier: 1.0 },
] as const;

export type JewelryMaterialId = typeof JEWELRY_MATERIALS[number]["id"];

export const JEWELRY_GAUGES = [
  { id: "20g", label: "20 Gauge (0.8mm)", description: "Fine jewelry · Nostril, helix" },
  { id: "18g", label: "18 Gauge (1.0mm)", description: "Standard · Most ear piercings" },
  { id: "16g", label: "16 Gauge (1.2mm)", description: "Common · Cartilage, tragus, eyebrow" },
  { id: "14g", label: "14 Gauge (1.6mm)", description: "Standard · Navel, tongue, nipple" },
  { id: "12g", label: "12 Gauge (2.0mm)", description: "Larger gauge · Stretched piercings" },
  { id: "ask", label: "Not sure — ask the piercer", description: "We'll recommend the right gauge" },
] as const;

export type JewelryGaugeId = typeof JEWELRY_GAUGES[number]["id"];

export const JEWELRY_STYLES = [
  { id: "stud", label: "Stud / Flat-back", description: "Classic · Versatile for most placements" },
  { id: "hoop", label: "Hoop / Seamless Ring", description: "Round · Great for healed piercings" },
  { id: "curved_barbell", label: "Curved Barbell", description: "Eyebrow, navel, rook" },
  { id: "straight_barbell", label: "Straight Barbell", description: "Industrial, tongue" },
  { id: "circular_barbell", label: "Circular Barbell (Horseshoe)", description: "Septum, eyebrow" },
  { id: "clicker", label: "Clicker Ring", description: "Easy to insert · Septum, daith" },
  { id: "ask", label: "Not sure — let the piercer decide", description: "We'll pick the best style for healing" },
] as const;

export type JewelryStyleId = typeof JEWELRY_STYLES[number]["id"];

export function calculatePiercingEstimate(
  piercingType: string,
  jewelryMaterial: string,
  piercingCount: number,
  pricingData: Array<{ piercingType: string; priceMin: number; priceMax: number; jewelryUpcharge?: number }>,
): { low: number; high: number } | null {
  const pricing = pricingData.find((p) => p.piercingType === piercingType)
    ?? PIERCING_TYPES.find((t) => t.id === piercingType);

  if (!pricing) return null;

  const materialMultiplier =
    JEWELRY_MATERIALS.find((m) => m.id === jewelryMaterial)?.multiplier ?? 1.0;

  const base = piercingCount > 0 ? piercingCount : 1;
  const upcharge = pricing.jewelryUpcharge ?? 0;
  const low = Math.round((pricing.priceMin + upcharge) * materialMultiplier * base / 5) * 5;
  const high = Math.round((pricing.priceMax + upcharge) * materialMultiplier * base / 5) * 5;

  return { low: Math.max(low, 30), high: Math.max(high, 40) };
}
